import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Link as MuiLink,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TranslateIcon from "@mui/icons-material/Translate";
import CheckIcon from "@mui/icons-material/Check";

import type { LegalDocumentId } from "../../legal/documents";
import { resolveDocumentLanguage } from "../../legal/documents";
import {
  DOCUMENT_LANGUAGES,
  LANGUAGE_LABELS,
  isDocumentLanguage,
  loadLegalContent,
  type DocumentLanguage,
  type LegalBlock,
  type LegalSection,
  type LoadedLegalContent,
} from "../../legal/content";

/**
 * Page publique d'un document contractuel.
 *
 * PUBLIQUE, sans coquille applicative. On y arrive depuis le formulaire
 * d'inscription, donc avant d'avoir un compte : la monter sous AppLayout la
 * rendrait inaccessible a la personne qui en a le plus besoin.
 *
 * LE TEXTE, RIEN QUE LE TEXTE
 * ---------------------------
 * Pas de bouton de telechargement, pas de fichier recupere depuis le serveur.
 * Un consentement se lit la ou on le donne : renvoyer vers un .docx qui
 * s'ouvre dans Word -- et nulle part sur mobile -- revient a demander de
 * cocher sans avoir lu, ce que le RGPD refuse de reconnaitre pour un DPA. Le
 * texte est embarque dans l'application et affiche ici.
 */
type Props = {
  document: LegalDocumentId;
};

/** Un bloc de corps : paragraphe, liste a puces ou liste numerotee. */
const Block: React.FC<{ block: LegalBlock }> = ({ block }) => {
  if (block.type === "p") {
    return (
      <Typography
        sx={{
          fontSize: 14.5,
          lineHeight: 1.75,
          mb: 1.5,
          color: "text.secondary",
          // Les adresses postales portent de vrais sauts de ligne, conserves
          // par le convertisseur. Sans ceci elles s'affichent sur une ligne.
          whiteSpace: "pre-line",
        }}
      >
        {block.text}
      </Typography>
    );
  }

  return (
    <Box component={block.type === "ol" ? "ol" : "ul"} sx={{ pl: 3, mb: 1.5, mt: 0 }}>
      {block.items.map((item, index) => (
        <Typography
          key={index}
          component="li"
          sx={{ fontSize: 14.5, lineHeight: 1.75, mb: 0.75, color: "text.secondary" }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  );
};

/**
 * Rend une section et ses sous-sections.
 *
 * Recursif parce qu'un article contractuel se subdivise (« 5 », puis « 5.1 »,
 * « 5.2 »). La profondeur decale le titre et reduit sa taille, sans jamais
 * descendre sous 15px : un alinea de DPA reste du texte a lire, pas une note
 * de bas de page.
 */
const Section: React.FC<{ section: LegalSection; depth?: number }> = ({
  section,
  depth = 0,
}) => (
  <Box component="section" sx={{ mt: depth === 0 ? 4 : 2.5, ml: depth * 2 }}>
    <Typography
      component={depth === 0 ? "h2" : "h3"}
      /* Ancre stable : /legal/terms#section-15.3 pointe directement l'alinea,
         ce qui rend un article citable dans un echange avec un client. */
      id={section.number ? `section-${section.number}` : undefined}
      sx={{
        fontSize: depth === 0 ? 18 : 15,
        fontWeight: 600,
        mb: 1,
        scrollMarginTop: 24,
      }}
    >
      {section.number ? `${section.number}. ` : ""}
      {section.heading}
    </Typography>

    {section.blocks?.map((block, index) => (
      <Block key={index} block={block} />
    ))}

    {section.children?.map((child, index) => (
      <Section key={child.number ?? index} section={child} depth={depth + 1} />
    ))}
  </Box>
);

export const LegalDocumentPage: React.FC<Props> = ({ document }) => {
  const { t, i18n } = useTranslation();

  /**
   * Langue du DOCUMENT, distincte de celle de l'interface.
   *
   * Elle part de la langue affichee, mais le menu la change sans toucher a
   * i18n : quelqu'un qui travaille en francais peut vouloir lire la version
   * anglaise, qui est la seule contractuelle. Basculer toute l'application
   * pour lire une clause serait disproportionne, et ferait perdre le
   * formulaire d'inscription reste ouvert dans l'autre onglet.
   */
  const initialLanguage = resolveDocumentLanguage(i18n.language);
  const [language, setLanguage] = useState<DocumentLanguage>(
    isDocumentLanguage(initialLanguage) ? initialLanguage : "en"
  );
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const [loaded, setLoaded] = useState<LoadedLegalContent | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    /*
     * `cancelled` plutot qu'un AbortController : il n'y a plus de requete
     * reseau a annuler, seulement un fragment de bundle a charger. On protege
     * uniquement contre l'ordre d'arrivee -- sans quoi, en changeant vite de
     * langue, la reponse de l'ancienne peut arriver apres la nouvelle et
     * l'ecraser.
     */
    let cancelled = false;

    setLoaded(null);
    setFailed(false);

    loadLegalContent(document, language)
      .then((result) => {
        if (!cancelled) setLoaded(result);
      })
      .catch((error) => {
        if (cancelled) return;
        setFailed(true);
        // eslint-disable-next-line no-console
        console.error("[legal] chargement impossible", error);
      });

    return () => {
      cancelled = true;
    };
  }, [document, language]);

  // Suit la langue de l'interface tant que l'utilisateur n'a pas choisi lui-meme.
  const [pinned, setPinned] = useState(false);
  useEffect(() => {
    if (pinned) return;
    const next = resolveDocumentLanguage(i18n.language);
    if (isDocumentLanguage(next)) setLanguage(next);
  }, [i18n.language, pinned]);

  const content = loaded?.content;
  const title =
    content?.title ??
    (document === "terms"
      ? t("legal.termsTitle", "Terms of Service")
      : t("legal.dpaTitle", "Data Processing Agreement"));

  const selectLanguage = (next: DocumentLanguage) => {
    setPinned(true);
    setLanguage(next);
    setMenuAnchor(null);
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", py: { xs: 3, md: 6 } }}>
      <Container maxWidth="md">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
          mb={3}
        >
          {/*
            Retour vers l'inscription, pas vers l'accueil.
            La page s'ouvre dans un onglet neuf depuis le formulaire, donc le
            bouton « precedent » du navigateur ne ramene nulle part. Sans ce
            lien, l'onglet est un cul-de-sac.
          */}
          <MuiLink
            component={RouterLink}
            to="/register"
            underline="hover"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 14 }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            {t("legal.backToSignup", "Back to sign-up")}
          </MuiLink>

          <Button
            size="small"
            startIcon={<TranslateIcon />}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
          >
            {LANGUAGE_LABELS[language]}
          </Button>

          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
          >
            {DOCUMENT_LANGUAGES.map((code) => (
              <MenuItem
                key={code}
                selected={code === language}
                onClick={() => selectLanguage(code)}
                sx={{ gap: 1.5, minWidth: 190 }}
              >
                <Box sx={{ width: 18, display: "flex", alignItems: "center" }}>
                  {code === language && <CheckIcon sx={{ fontSize: 18 }} />}
                </Box>
                {LANGUAGE_LABELS[code]}
                {/* L'anglais est signale : c'est la seule version opposable, et
                    le lecteur doit pouvoir la retrouver sans lire les clauses
                    finales. */}
                {code === "en" && (
                  <Typography
                    component="span"
                    sx={{ ml: "auto", fontSize: 11, color: "text.secondary" }}
                  >
                    {t("legal.controlling", "official")}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Menu>
        </Stack>

        <Typography component="h1" sx={{ fontSize: 28, fontWeight: 700, mb: 1 }}>
          {title}
        </Typography>

        {content && (
          <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: 2 }}>
            {content.publisher}
            {content.effectiveDate ? ` — ${content.effectiveDate}` : ""}
            {content.subtitle ? ` — ${content.subtitle}` : ""}
          </Typography>
        )}

        {/*
          La version anglaise prevaut : c'est ecrit dans les documents
          eux-memes. Quelqu'un qui lit une traduction de courtoisie a le droit
          de le savoir avant de cocher, pas de le decouvrir dans une clause
          finale.
        */}
        {loaded?.fellBackToEnglish && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t(
              "legal.englishOnly",
              "This document is not available in your language, so the English version is shown. The English version prevails in any case."
            )}
          </Alert>
        )}

        <Divider sx={{ mb: 1 }} />

        {!loaded && !failed && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={26} />
          </Box>
        )}

        {/*
          Echec de chargement : on ne laisse pas une page vide.
          Un titre suivi de rien donnerait a croire que le contrat n'a pas de
          clauses, ce qui est pire qu'une erreur franche -- la personne
          cocherait la case en pensant avoir tout lu.
        */}
        {failed && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            {t(
              "legal.loadFailed",
              "This document could not be displayed. Please try another language, or contact support@codenektforfleet.com."
            )}
          </Alert>
        )}

        {content && (
          <>
            {content.intro?.map((block, index) => (
              <Box key={index} sx={{ mt: index === 0 ? 2 : 0 }}>
                <Block block={block} />
              </Box>
            ))}

            {content.sections.map((section, index) => (
              <Section key={section.number ?? index} section={section} />
            ))}
          </>
        )}
      </Container>
    </Box>
  );
};

export const TermsPage: React.FC = () => <LegalDocumentPage document="terms" />;
export const DpaPage: React.FC = () => <LegalDocumentPage document="dpa" />;

export default LegalDocumentPage;
